import styled from '@emotion/styled'
import { useDragControls } from 'framer-motion'
import { Reorder } from 'framer-motion'
import Link from 'next/link'
import React from 'react'

import RuleConditions from './RuleConditions'
import Button from 'components/Button'
import TemplateInitialsSquare from 'components/TemplateInitialsSquare'
import { DragIcon, TrashIcon } from 'components/icons'
import Tick from 'components/theming/radio/Tick'
import { getOrdinalSuffix, isMobile } from 'lib/helpers'
import { Rule } from 'types/rules'
import { Template } from 'types/template'

const RuleTitle = styled.div`
	display: flex;
	align-items: center;
	gap: ${({ theme }) => theme.spaces.space3};
	margin-bottom: ${({ theme }) => theme.spaces.space5};
`

const RuleOrder = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	background-color: #e6e6fa;
	border-radius: ${({ theme }) => theme.borderRadiuses.xxl};
	${({ theme }) => theme.typography.captionMedium};
	padding: ${({ theme }) => `${theme.spaces.space2} ${theme.spaces.space4}`};
`

const RuleName = styled.span`
	${({ theme }) => theme.typography.captionMedium};
	overflow: hidden;
	max-width: 100%;
	text-wrap: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
`

const ReorderButton = styled.div`
	position: absolute;
	top: 10px;
	left: -30px;
	cursor: grab;

	svg {
		width: 24px;
		height: 24px;
		color: ${({ theme }) => theme.colors.light.secondaryTextColor};
	}
`

const DeleteButton = styled(Button)`
	position: absolute;
	top: ${({ theme }) => theme.spaces.space6};
	top: -10px;
	right: -50px;
	opacity: 0;
	transition: opacity 200ms ease;
	background-color: transparent;

	&:focus {
		opacity: 1;
	}
`

const TemplateSection = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${({ theme }) => theme.spaces.space2};
`

const TemplateInfo = styled.div`
	display: flex;
	align-items: center;
	gap: ${({ theme }) => theme.spaces.space3};
	width: 100%;
`

const TemplateText = styled.span`
	${({ theme }) => theme.typography.captionSmall};
	color: ${({ theme }) => theme.colors.light.grayscale[500]};
`

const TemplateName = styled.span`
	${({ theme }) => theme.typography.smallcaps};
	overflow: hidden;
	max-width: 100%;
	text-wrap: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
`

const RuleContent = styled.div`
	display: grid;
	grid-template-columns: 35px 190px 50px 190px 55px auto;
	gap: 0;
	align-items: start;
	margin-top: ${({ theme }) => theme.spaces.space6};
`

const TemplateColumn = styled.div`
	isplay: flex;
	flex-direction: column;
	gap: ${({ theme }) => theme.spaces.space2};
	padding: ${({ theme }) => `${theme.spaces.space2} ${theme.spaces.space3}`};
	max-width: 100%;
	overflow: hidden;
`

const StyledRuleCard = styled(Reorder.Item)<{ highlighted: boolean }>`
	padding: ${({ theme }) => theme.spaces.space6};
	background-color: ${({ theme }) => theme.colors.light.grayscale[15]};
	border-radius: ${({ theme }) => theme.borderRadiuses.xl};
	margin-bottom: ${({ theme }) => theme.spaces.space4};
	box-shadow: ${({ theme }) => theme.boxShadows.subtle};
	width: 700px;
	list-style: none;
	position: relative;
	border: 1px solid
		${({ theme, highlighted }) => (highlighted ? theme.colors.light.green[100] : theme.colors.light.borderColor)};
	transition: background-color 200ms ease;

	&:hover {
		background-color: ${({ theme }) => theme.colors.light.surface};

		${DeleteButton} {
			opacity: 1;
		}
	}
`

const BaseLink = styled(Link)`
	text-decoration: none;
	color: inherit;
	position: absolute;
	inset: 0;
`

type RuleCardProps = {
	rule: Rule
	templates: Template[]
	onDragEnd: () => void
	onKeyboardReorder: (event: React.KeyboardEvent) => void
	onDelete: (id: string) => void
	index: number
	highlighted: boolean
}

function RuleCard({ rule, templates, onDragEnd, onKeyboardReorder, onDelete, index, highlighted }: RuleCardProps) {
	const cardRef = React.useRef<HTMLDivElement>(null)
	const controls = useDragControls()
	const template = templates?.find((template) => template.id === rule.templateId)
	const urlConditions = rule.conditions.filter((condition) => condition.extractor === 'url')
	const domainConditions = rule.conditions.filter((condition) => condition.extractor === 'domain')

	React.useEffect(() => {
		if (highlighted && cardRef.current) {
			cardRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' })
		}
	}, [highlighted])

	return (
		<StyledRuleCard
			value={rule.id}
			dragControls={controls}
			dragListener={isMobile()}
			onDragEnd={onDragEnd}
			onKeyDown={onKeyboardReorder}
			data-testid={`rule-card-${index}`}
			highlighted={highlighted}
			ref={cardRef}
		>
			{highlighted && <Tick shouldAnimate />}
			<ReorderButton
				role="button"
				tabIndex={0}
				onPointerDown={(event) => {
					controls.start(event)
				}}
				onKeyDown={onKeyboardReorder}
				aria-label="Change rule position"
			>
				<DragIcon />
			</ReorderButton>

			{!highlighted && (
				<DeleteButton
					kind="text"
					type="button"
					onClick={(e) => {
						e.preventDefault()
						onDelete(rule.id)
					}}
					aria-label="Delete rule"
					disableHover
				>
					<TrashIcon />
				</DeleteButton>
			)}

			<BaseLink href={`/advanced-settings/rules/${rule.id}`} aria-label={`go to ${rule.name} rule configuration`} />

			<RuleTitle>
				<RuleOrder>{getOrdinalSuffix(index + 1)}</RuleOrder>
				<RuleName>{rule.name}</RuleName>
			</RuleTitle>

			<RuleContent>
				<RuleConditions urlConditions={urlConditions} domainConditions={domainConditions} />

				<TemplateColumn>
					<TemplateSection>
						<TemplateText>Use template</TemplateText>
						<TemplateInfo>
							<TemplateInitialsSquare name={template?.name ?? ''} />
							<TemplateName>{template?.name}</TemplateName>
						</TemplateInfo>
					</TemplateSection>
				</TemplateColumn>
			</RuleContent>
		</StyledRuleCard>
	)
}

export default RuleCard
